import { noticeParts } from '../../lib/notice-link';
import { formatMoney } from '../../lib/money';
import { DocLogo } from './DocLogo';
import { invoiceTerms, PO_BUYER, type InvoicePreviewLine } from './InvoicePreview';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function longDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!match) return '';
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1] || ''} ${match[1]}`.trim();
}

function RichText({ value }: { value: string }) {
  return (
    <span className="whitespace-pre-line">
      {noticeParts(value).map((part, index) => part.href
        ? <a key={index} href={part.href} className="underline" style={{ color: '#1155cc' }} target="_blank" rel="noreferrer">{part.text}</a>
        : <span key={index}>{part.text}</span>)}
    </span>
  );
}

const BANK: [string, string][] = [
  ['Bank', 'Revolut Business'],
  ['Account', 'MaxIron Ltd'],
  ['Account no.', '40300404'],
  ['Sort code', '040075'],
  ['IBAN', 'GB13REVO00996970545792'],
];

export function InvoiceFormat2({
  documentTitle = 'INVOICE',
  number,
  customerName,
  billingEmails,
  billToName = '',
  billToAddress = '',
  billToEmail = '',
  billToDetails = '',
  bankLines,
  title = '',
  issuedOn,
  dueOn,
  currency,
  poNumber,
  contactName,
  contactRole,
  contactEmail,
  contactPhone,
  validDays = 0,
  paymentTerms = '',
  delivery = '',
  acceptance = '',
  purpose = '',
  scope = '',
  notice,
  lines,
  vatPence,
}: {
  documentTitle?: string;
  number?: string;
  customerName: string;
  billingEmails: string[];
  billToName?: string;
  billToAddress?: string;
  billToEmail?: string;
  billToDetails?: string;
  bankLines?: [string, string][];
  title?: string;
  issuedOn: string;
  dueOn: string;
  currency: string;
  poNumber: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  validDays?: number;
  paymentTerms?: string;
  delivery?: string;
  acceptance?: string;
  purpose?: string;
  scope?: string;
  notice: string;
  lines: InvoicePreviewLine[];
  vatPence?: number | null;
}) {
  const money = currency || 'GBP';
  const shown = lines.filter((line) => line.description || line.amountPence);
  const net = shown.reduce((sum, line) => sum + (line.amountPence || 0), 0);
  const vat = vatPence ?? 0;
  const vatLabel = vat !== 0 && net > 0 && Math.abs(vat - Math.round(net * 0.2)) <= 2 ? 'VAT @20%' : 'VAT';
  const gross = net + (vat === 0 ? 0 : vat);
  const ref = number || 'Draft';
  const contacts = [contactName, contactRole, contactEmail, contactPhone].filter(Boolean);
  const invoiceDocument = documentTitle === 'INVOICE' || documentTitle === 'CREDIT NOTE';
  const purchaseOrder = documentTitle === 'PURCHASE ORDER';
  const showParty = invoiceDocument || purchaseOrder;
  const address = billToAddress.split(/\n|,/).map((part) => part.trim()).filter(Boolean);
  const emails = showParty && billToEmail ? [billToEmail] : billingEmails;
  const recipient = billToName || 'Accounts Payable';
  const terms = showParty ? invoiceTerms({ validDays, paymentTerms, delivery, purpose, scope, notice, acceptance }) : [];
  const bank = bankLines?.length ? bankLines : BANK;
  const showLineOrder = documentTitle === 'INVOICE' || shown.some((line) => line.orderRef);

  return (
    <article className="doc-q2">
      <header className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <DocLogo />
          <p className="mt-2 text-[9pt] leading-snug" style={{ color: '#655e70' }}>
            <strong className="block text-[11pt] font-bold" style={{ color: '#24212d' }}>Maxiron Ltd</strong>
            3rd Floor 86-90 Paul Street, London, EC2A 4NE<br />
            Company No. 14444817 · VAT No. 435737379<br />
            <a className="underline" href="mailto:office@maxiron.com">office@maxiron.com</a>
          </p>
        </div>
        <div className="shrink-0 text-right">
          <h1 className="text-[18pt] font-bold tracking-wide">{documentTitle}</h1>
          <p className="mt-1 text-[9pt] leading-snug" style={{ color: '#655e70' }}>{documentTitle === 'CREDIT NOTE' ? 'Credit note' : 'Reference'} <b className="font-bold" style={{ color: '#27212e' }}>{ref}</b></p>
          <p className="text-[9pt]" style={{ color: '#655e70' }}>Date <b className="font-bold" style={{ color: '#27212e' }}>{longDate(issuedOn)}</b></p>
        </div>
      </header>
      <section className="mt-5 grid grid-cols-2 gap-6 border-t pt-4" style={{ borderColor: '#e9e4ee' }}>
        {purchaseOrder ? (
          <>
            <div>
              <p className="eyebrow mb-2">Supplier</p>
              <p className="text-[10pt] leading-relaxed">
                <strong className="font-bold">{billToName || customerName || 'Supplier'}</strong><br />
                {address.map((line) => <span key={line}>{line}<br /></span>)}
                {emails.map((email) => <span key={email}><a className="underline" href={`mailto:${email}`}>{email}</a><br /></span>)}
              </p>
            </div>
            <div>
              <p className="eyebrow mb-2">Bill to</p>
              <p className="text-[10pt] leading-relaxed">
                <strong className="font-bold">{PO_BUYER.name}</strong><br />
                {PO_BUYER.address}<br />
                {PO_BUYER.company} · {PO_BUYER.vat}<br />
                <a className="underline" href={`mailto:${PO_BUYER.email}`}>{PO_BUYER.email}</a>
              </p>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="eyebrow mb-2">Bill to</p>
              <p className="text-[10pt] leading-relaxed">
                <strong className="font-bold">{showParty ? recipient : 'Accounts Payable'}</strong><br />
                {(!showParty || !address.length) && customerName ? <>{customerName}<br /></> : null}
                {showParty ? address.map((line) => <span key={line}>{line}<br /></span>) : null}
                {emails.map((email) => <span key={email}><a className="underline" href={`mailto:${email}`}>{email}</a><br /></span>)}
              </p>
            </div>
            <div>
              <p className="eyebrow mb-2">Details</p>
              {invoiceDocument && title ? <p className="text-[10pt]"><span style={{ color: '#655e70' }}>Title </span><strong className="font-bold">{title}</strong></p> : null}
              {documentTitle !== 'INVOICE' && poNumber ? <p className="text-[10pt]"><span style={{ color: '#655e70' }}>Purchase order </span><strong className="font-bold">{poNumber}</strong></p> : null}
              {dueOn ? <p className="text-[10pt]"><span style={{ color: '#655e70' }}>Due </span><strong className="font-bold">{longDate(dueOn)}</strong></p> : null}
              {invoiceDocument && billToDetails ? <p className="mt-2 whitespace-pre-line text-[10pt]">{billToDetails}</p> : null}
            </div>
          </>
        )}
      </section>
      {contacts.length ? (
        <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 rounded px-3 py-2 text-[9pt]" style={{ background: '#f6f3fa' }}>
          {contactName ? <strong className="font-bold">{contactName}</strong> : null}
          {contactRole ? <span style={{ color: '#76687f' }}>{contactRole}</span> : null}
          {contactEmail ? <a className="underline" href={`mailto:${contactEmail}`}>{contactEmail}</a> : null}
          {contactPhone ? <span>{contactPhone}</span> : null}
        </p>
      ) : null}
      <table className="mt-4">
        <thead>
          <tr>
            <th className="qty">Qty</th>
            {showLineOrder ? <th className="po">PO / line</th> : null}
            <th>Description</th>
            <th className="num">Unit price</th>
            <th className="num">Line total</th>
          </tr>
        </thead>
        <tbody>
          {(shown.length ? shown : [{ qty: 0, description: '', unitPence: 0, amountPence: 0 }]).map((line, index) => (
            <tr key={index}>
              <td className="qty">{line.qty || ''}</td>
              {showLineOrder ? <td className="po">{line.orderRef || ''}</td> : null}
              <td className="whitespace-pre-line font-bold">{line.description}</td>
              <td className="num">{line.unitPence > 0 ? formatMoney(line.unitPence, money) : line.description ? '-' : ''}</td>
              <td className="num">{line.amountPence ? formatMoney(line.amountPence, money) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 ml-auto w-56 text-[10pt]">
        <div className="flex justify-between px-2 py-1"><span>Subtotal</span><span>{formatMoney(net, money)}</span></div>
        <div className="flex justify-between px-2 py-1"><span className={vat === 0 ? 'font-bold' : ''}>{vatLabel}</span><span className={vat === 0 ? 'font-bold' : ''}>{vat === 0 ? 'NONE' : formatMoney(vat, money)}</span></div>
        <div className="mt-1 flex justify-between px-2 py-2 text-[12pt] font-bold" style={{ background: '#f6f0fb', color: '#522479', borderTop: '1px solid #d9c6e9' }}>
          <span>Total {money}</span><span>{formatMoney(gross, money)}</span>
        </div>
      </div>
      {purchaseOrder ? (
        <p className="mt-6 border-t pt-4 text-[9pt]" style={{ borderColor: '#ded5e6', color: '#554d61' }}>Please invoice Maxiron Ltd and quote this purchase order number. This order is not a request for payment.</p>
      ) : (
      <section className="mt-6 border-t pt-4" style={{ borderColor: '#ded5e6' }}>
        <h2 className="mb-3 text-[12pt] font-bold">Bank details</h2>
        <dl className="grid grid-cols-[7.2rem_1fr] gap-x-4 gap-y-1 text-[9pt]">
          {bank.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="font-bold">{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>
      )}
      {terms.length ? (
        <section className="mt-5">
          <h2 className="mb-2 text-[12pt] font-bold">Commercial terms</h2>
          <dl className="grid grid-cols-[7.2rem_1fr] gap-x-3 gap-y-1 text-[9pt]">
            {terms.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="font-bold">{label}</dt>
                <dd style={{ color: '#554d61' }}><RichText value={value} /></dd>
              </div>
            ))}
          </dl>
        </section>
      ) : notice.trim() ? (
        <section className="mt-5">
          <h2 className="mb-2 text-[12pt] font-bold">Terms</h2>
          <p className="text-[9pt] leading-relaxed" style={{ color: '#554d61' }}><RichText value={notice} /></p>
        </section>
      ) : null}
      <footer className="mt-auto pt-6">
        <p className="mb-4 text-center text-[10pt]" style={{ color: '#6b577b' }}>{purchaseOrder ? 'Thank you.' : 'Thank you for your business.'}</p>
        <div className="flex justify-between gap-3 border-t pt-2 text-[8pt]" style={{ borderColor: '#e9e4ee', color: '#847b8d' }}>
          <span>Maxiron Ltd · maxiron.com · office@maxiron.com</span>
          <span>{ref} · Page 1 of 1</span>
        </div>
      </footer>
    </article>
  );
}
